import { useEffect, useState } from "react";
import { Bot, Loader2, MessageCircle, Search, Send } from "lucide-react";
import {
  copilotQuery,
  copilotSearch,
  setComplianceContext,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { SemanticSearchResultItem } from "../../types/compliance";
import ShipmentCasePicker from "./ShipmentCasePicker";

type Message = {
  role: "user" | "assistant";
  text: string;
  confidence?: number;
  citations?: string[];
};

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const classes =
    pct >= 80
      ? "bg-green-50 border-green-200 text-green-700"
      : pct >= 50
        ? "bg-amber-50 border-amber-200 text-amber-700"
        : "bg-red-50 border-red-200 text-red-700";
  return (
    <div
      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${classes}`}
    >
      {pct}% confidence
    </div>
  );
}

export default function CopilotPanel() {
  const { credentials } = useComplianceAuth();
  const [tenantId, setTenantId] = useState(
    () => credentials?.tenantId ?? "default",
  );
  const [workflowId, setWorkflowId] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [activeTab, setActiveTab] = useState<"chat" | "search">("chat");

  // Chat state
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [topK, setTopK] = useState(5);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SemanticSearchResultItem[]>([]);
  const [searchBackend, setSearchBackend] = useState("");
  const [searchTotal, setSearchTotal] = useState(0);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setComplianceContext(tenantId, credentials?.role ?? "COMPLIANCE_AUDITOR");
  }, [tenantId, credentials?.role]);

  async function submit() {
    if (!workflowId.trim() || !prompt.trim()) {
      setError("Workflow ID and question are required.");
      return;
    }
    setLoading(true);
    setError(null);
    const question = prompt.trim();
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setPrompt("");
    try {
      const response = await copilotQuery({
        tenant_id: tenantId,
        workflow_id: workflowId.trim(),
        question,
        batch_id: selectedCaseId || undefined,
      });
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: response.answer,
          confidence: response.confidence,
          citations: response.citations,
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Copilot request failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    setError(null);
    setSearchResults([]);
    try {
      const res = await copilotSearch({
        tenant_id: tenantId,
        query: searchQuery.trim(),
        workflow_id: workflowId || undefined,
        batch_id: selectedCaseId || undefined,
        top_k: topK,
      });
      setSearchResults(res.results);
      setSearchBackend(res.backend);
      setSearchTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setSearchLoading(false);
    }
  }

  return (
    <div className="p-6 overflow-y-auto h-full">
      <div className="flex items-center gap-2 mb-6">
        <Bot size={24} className="text-emerald-600" />
        <h1 className="text-2xl font-bold text-gray-900">Compliance Copilot</h1>
      </div>

      {/* Context row */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          placeholder="Tenant ID"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <input
          value={workflowId}
          onChange={(e) => setWorkflowId(e.target.value)}
          placeholder="Workflow ID (optional for search)"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={() => {
            setMessages([]);
            setSearchResults([]);
            setError(null);
          }}
          className="text-sm bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-2"
        >
          Clear Session
        </button>
      </div>

      <div className="mb-4">
        <ShipmentCasePicker
          selectedCaseId={selectedCaseId}
          selectedWorkflowId={workflowId || undefined}
          onSelectCase={(shipmentCase, detail) => {
            setSelectedCaseId(shipmentCase.case_id);
            setWorkflowId(detail.documents[0]?.workflow_id ?? "");
            setSearchQuery(shipmentCase.case_id);
            setPrompt(`Review shipment case ${shipmentCase.case_id} and summarize missing documents, mismatches, risk signals, and next compliance actions.`);
            setActiveTab("search");
          }}
          onSelectWorkflow={(document, shipmentCase) => {
            const label = document.document_type.replace(/_/g, " ");
            setSelectedCaseId(shipmentCase.case_id);
            setWorkflowId(document.workflow_id);
            setSearchQuery(`${shipmentCase.case_id} ${label}`);
            setPrompt(`Review the ${label} workflow in shipment case ${shipmentCase.case_id}. Summarize risks, missing fields, mismatches, and next compliance actions.`);
            setActiveTab("chat");
          }}
        />
        {selectedCaseId && (
          <p className="mt-2 text-xs text-gray-500">
            Active shipment case copilot scope: <span className="font-mono">{selectedCaseId}</span>. Select a document workflow above to pre-fill chat and semantic search for that file.
          </p>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          type="button"
          onClick={() => setActiveTab("chat")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === "chat"
              ? "bg-white shadow text-emerald-700"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <MessageCircle size={14} />
          Chat
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("search")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition ${
            activeTab === "search"
              ? "bg-white shadow text-emerald-700"
              : "text-gray-600 hover:text-gray-800"
          }`}
        >
          <Search size={14} />
          Semantic Search
        </button>
      </div>

      {/* ── Chat ─────────────────────────────────────────────────────────── */}
      {activeTab === "chat" && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="h-[420px] overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.length === 0 && (
              <p className="text-sm text-gray-500">
                Ask for workflow risk explanations, validation summaries, or
                next review actions.
              </p>
            )}
            {messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}`}
                className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : ""}`}
              >
                <div
                  className={`rounded-lg px-3 py-2 text-sm ${
                    m.role === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {m.text}
                </div>
                {m.role === "assistant" && m.confidence !== undefined && (
                  <ConfidenceBar value={m.confidence} />
                )}
                {m.role === "assistant" &&
                  m.citations &&
                  m.citations.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {m.citations.map((c, i) => (
                        <span
                          key={i}
                          className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-gray-200">
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <div className="flex gap-2">
              <input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder="What should I review next for this workflow?"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => void submit()}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm px-4 py-2 rounded-lg"
              >
                {loading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Semantic Search ───────────────────────────────────────────────── */}
      {activeTab === "search" && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
          <div className="flex gap-3 mb-4">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSearch();
              }}
              placeholder="Search compliance documents, risks, audit events…"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
            />
            <select
              value={topK}
              aria-label="Number of results"
              onChange={(e) => setTopK(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-2 text-sm"
            >
              {[3, 5, 10].map((n) => (
                <option key={n} value={n}>
                  Top {n}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => void handleSearch()}
              disabled={searchLoading}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white text-sm px-4 py-2 rounded-lg"
            >
              {searchLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Search size={14} />
              )}
              Search
            </button>
          </div>

          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          {searchResults.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>
                  {searchTotal} result{searchTotal !== 1 ? "s" : ""}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${
                    searchBackend === "qdrant"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {searchBackend === "qdrant"
                    ? "Vector (Qdrant)"
                    : "Keyword fallback"}
                </span>
              </div>
              {searchResults.map((r, i) => (
                <div
                  key={r.chunk_id ?? i}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-700 capitalize">
                      {r.source_type}
                    </span>
                    <span
                      className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        r.score >= 0.8
                          ? "bg-emerald-100 text-emerald-700"
                          : r.score >= 0.5
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {(r.score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    {r.content}
                  </p>
                  {r.workflow_id && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      Workflow: {r.workflow_id}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {searchResults.length === 0 && !searchLoading && searchQuery && (
            <p className="text-sm text-gray-500 text-center py-8">
              No results found. Try a different query.
            </p>
          )}

          {searchResults.length === 0 && !searchLoading && !searchQuery && (
            <p className="text-sm text-gray-500 text-center py-8">
              Search across indexed compliance documents, risk records, and
              audit events using vector similarity.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
